/**
 * CRM Sync XState machines - UserManagement, BusinessSettings, AudioFile, Billing, Access, CallFlow
 */
import { setup, assign } from 'xstate'

// ============ User Management ============
export interface CrmUser {
  id: string
  name: string
  email: string
  phone?: string
  avatar?: string
  role: string
  status: 'active' | 'inactive' | 'suspended'
  createdAt: string
  updatedAt?: string
}

export type CrmSyncUserEvent =
  | { type: 'SET_USERS'; users: CrmUser[] }
  | { type: 'ADD_USER'; user: CrmUser }
  | { type: 'UPDATE_USER'; id: string; updates: Partial<CrmUser> }
  | { type: 'REMOVE_USER'; id: string }
  | { type: 'SET_LOADING'; loading: boolean }

const userSetup = setup({
  types: { context: {} as { users: CrmUser[]; isLoading: boolean }, events: {} as CrmSyncUserEvent },
})

export const crmSyncUserMachine = userSetup.createMachine({
  id: 'crmSyncUser',
  context: { users: [], isLoading: false },
  on: {
    SET_USERS: {
      actions: assign({
        users: ({ event }) => (event.type === 'SET_USERS' ? event.users : []),
      }),
    },
    ADD_USER: {
      actions: assign({
        users: ({ context, event }) =>
          event.type === 'ADD_USER' ? [...context.users, event.user] : context.users,
      }),
    },
    UPDATE_USER: {
      actions: assign({
        users: ({ context, event }) =>
          event.type === 'UPDATE_USER'
            ? context.users.map((u) => (u.id === event.id ? { ...u, ...event.updates } : u))
            : context.users,
      }),
    },
    REMOVE_USER: {
      actions: assign({
        users: ({ context, event }) =>
          event.type === 'REMOVE_USER'
            ? context.users.filter((u) => u.id !== event.id)
            : context.users,
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
  },
})

// ============ Business Settings ============
export interface CrmBusinessSettings {
  id?: string
  companyName: string
  phone: string
  email: string
  website: string
  timezone: string
  workingDays: Record<string, boolean>
  workStart: string
  workEnd: string
  lunchStart: string
  lunchEnd: string
  enableLunch: boolean
  chatQueue?: string
  onOpenCallFlow?: string
  onClosedCallFlow?: string
  schedule?: Array<{
    day: number
    periods: Array<{
      startHour: number
      startMinute: number
      endHour: number
      endMinute: number
    }>
    timeZoneIdentifier: string
  }>
}

const defaultBusinessSettings: CrmBusinessSettings = {
  companyName: '',
  phone: '',
  email: '',
  website: '',
  timezone: 'Europe/Moscow',
  workingDays: {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
  },
  workStart: '09:00',
  workEnd: '18:00',
  lunchStart: '13:00',
  lunchEnd: '14:00',
  enableLunch: true,
}

export type CrmSyncBusinessEvent =
  | { type: 'SET_SETTINGS'; settings: CrmBusinessSettings }
  | { type: 'UPDATE_SETTINGS'; updates: Partial<CrmBusinessSettings> }
  | { type: 'SET_LOADING'; loading: boolean }

const businessSetup = setup({
  types: {
    context: {} as { settings: CrmBusinessSettings | null; isLoading: boolean },
    events: {} as CrmSyncBusinessEvent,
  },
})

export const crmSyncBusinessMachine = businessSetup.createMachine({
  id: 'crmSyncBusiness',
  context: { settings: null, isLoading: false },
  on: {
    SET_SETTINGS: {
      actions: assign({
        settings: ({ event }) => (event.type === 'SET_SETTINGS' ? event.settings : null),
      }),
    },
    UPDATE_SETTINGS: {
      actions: assign({
        settings: ({ context, event }) => {
          if (event.type !== 'UPDATE_SETTINGS') return context.settings
          return context.settings
            ? { ...context.settings, ...event.updates }
            : { ...defaultBusinessSettings, ...event.updates }
        },
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
  },
})

// ============ Audio File ============
export interface CrmAudioFile {
  id: string
  name: string
  description?: string
  fileId?: string
  language?: string
  text?: string
  type: 'greeting' | 'hold' | 'menu' | 'announcement' | 'other'
  duration: number
  size: number
  url?: string
  createdAt: string
  updatedAt: string
}

export type CrmSyncAudioEvent =
  | { type: 'SET_FILES'; files: CrmAudioFile[] }
  | { type: 'ADD_FILE'; file: CrmAudioFile }
  | { type: 'UPDATE_FILE'; id: string; updates: Partial<CrmAudioFile> }
  | { type: 'REMOVE_FILE'; id: string }
  | { type: 'SET_LOADING'; loading: boolean }

const audioSetup = setup({
  types: {
    context: {} as { audioFiles: CrmAudioFile[]; isLoading: boolean },
    events: {} as CrmSyncAudioEvent,
  },
})

export const crmSyncAudioMachine = audioSetup.createMachine({
  id: 'crmSyncAudio',
  context: { audioFiles: [], isLoading: false },
  on: {
    SET_FILES: {
      actions: assign({
        audioFiles: ({ event }) => (event.type === 'SET_FILES' ? event.files : []),
      }),
    },
    ADD_FILE: {
      actions: assign({
        audioFiles: ({ context, event }) =>
          event.type === 'ADD_FILE'
            ? [...context.audioFiles, event.file]
            : context.audioFiles,
      }),
    },
    UPDATE_FILE: {
      actions: assign({
        audioFiles: ({ context, event }) =>
          event.type === 'UPDATE_FILE'
            ? context.audioFiles.map((f) => (f.id === event.id ? { ...f, ...event.updates } : f))
            : context.audioFiles,
      }),
    },
    REMOVE_FILE: {
      actions: assign({
        audioFiles: ({ context, event }) =>
          event.type === 'REMOVE_FILE'
            ? context.audioFiles.filter((f) => f.id !== event.id)
            : context.audioFiles,
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
  },
})

// ============ Billing Invoice ============
export interface BillingInvoiceItem {
  id: string
  description: string
  quantity: number
  unitPrice: number
  total: number
}

export interface BillingInvoice {
  id: string
  invoiceNumber: string
  customerId: string
  customerName: string
  amount: number
  currency: string
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'cancelled'
  dueDate: string
  paidAt?: string
  items: BillingInvoiceItem[]
  notes?: string
  createdAt: string
  updatedAt: string
}

const defaultStats = {
  totalRevenue: 0,
  monthlyRecurring: 0,
  activeSubscriptions: 0,
  pendingInvoices: 0,
}

export type CrmSyncBillingEvent =
  | { type: 'SET_INVOICES'; invoices: BillingInvoice[] }
  | { type: 'ADD_INVOICE'; invoice: BillingInvoice }
  | { type: 'UPDATE_INVOICE'; id: string; updates: Partial<BillingInvoice> }
  | { type: 'REMOVE_INVOICE'; id: string }
  | { type: 'SET_STATS'; stats: typeof defaultStats }
  | { type: 'SET_LOADING'; loading: boolean }

const billingSetup = setup({
  types: {
    context: {} as { invoices: BillingInvoice[]; isLoading: boolean; stats: typeof defaultStats },
    events: {} as CrmSyncBillingEvent,
  },
})

export const crmSyncBillingMachine = billingSetup.createMachine({
  id: 'crmSyncBilling',
  context: { invoices: [], isLoading: false, stats: defaultStats },
  on: {
    SET_INVOICES: {
      actions: assign({
        invoices: ({ event }) => (event.type === 'SET_INVOICES' ? event.invoices : []),
      }),
    },
    ADD_INVOICE: {
      actions: assign({
        invoices: ({ context, event }) =>
          event.type === 'ADD_INVOICE'
            ? [...context.invoices, event.invoice]
            : context.invoices,
      }),
    },
    UPDATE_INVOICE: {
      actions: assign({
        invoices: ({ context, event }) =>
          event.type === 'UPDATE_INVOICE'
            ? context.invoices.map((i) => (i.id === event.id ? { ...i, ...event.updates } : i))
            : context.invoices,
      }),
    },
    REMOVE_INVOICE: {
      actions: assign({
        invoices: ({ context, event }) =>
          event.type === 'REMOVE_INVOICE'
            ? context.invoices.filter((i) => i.id !== event.id)
            : context.invoices,
      }),
    },
    SET_STATS: {
      actions: assign({
        stats: ({ event }) => (event.type === 'SET_STATS' ? event.stats : defaultStats),
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
  },
})

// ============ Access ============
export interface AccessRole {
  id: string
  name: string
  description?: string
  permissions: string[]
  userCount: number
  isSystem: boolean
  createdAt: string
  updatedAt: string
}

export interface AccessPermission {
  id: string
  name: string
  description?: string
  module: string
  actions: string[]
}

export type CrmSyncAccessEvent =
  | { type: 'SET_ROLES'; roles: AccessRole[] }
  | { type: 'ADD_ROLE'; role: AccessRole }
  | { type: 'UPDATE_ROLE'; id: string; updates: Partial<AccessRole> }
  | { type: 'REMOVE_ROLE'; id: string }
  | { type: 'SET_PERMISSIONS'; permissions: AccessPermission[] }
  | { type: 'SET_LOADING'; loading: boolean }

const accessSetup = setup({
  types: {
    context: {} as { roles: AccessRole[]; permissions: AccessPermission[]; isLoading: boolean },
    events: {} as CrmSyncAccessEvent,
  },
})

export const crmSyncAccessMachine = accessSetup.createMachine({
  id: 'crmSyncAccess',
  context: { roles: [], permissions: [], isLoading: false },
  on: {
    SET_ROLES: {
      actions: assign({
        roles: ({ event }) => (event.type === 'SET_ROLES' ? event.roles : []),
      }),
    },
    ADD_ROLE: {
      actions: assign({
        roles: ({ context, event }) =>
          event.type === 'ADD_ROLE' ? [...context.roles, event.role] : context.roles,
      }),
    },
    UPDATE_ROLE: {
      actions: assign({
        roles: ({ context, event }) =>
          event.type === 'UPDATE_ROLE'
            ? context.roles.map((r) => (r.id === event.id ? { ...r, ...event.updates } : r))
            : context.roles,
      }),
    },
    REMOVE_ROLE: {
      actions: assign({
        roles: ({ context, event }) =>
          event.type === 'REMOVE_ROLE'
            ? context.roles.filter((r) => r.id !== event.id)
            : context.roles,
      }),
    },
    SET_PERMISSIONS: {
      actions: assign({
        permissions: ({ event }) =>
          event.type === 'SET_PERMISSIONS' ? event.permissions : [],
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
  },
})

// ============ Call Flow ============
export interface CrmCallFlow {
  id: string
  name: string
  description?: string
  type: 'inbound' | 'outbound' | 'internal'
  status: 'active' | 'inactive' | 'draft'
  nodes: number
  queueId?: string
  createdAt: string
  updatedAt: string
}

export type CrmSyncCallFlowEvent =
  | { type: 'SET_FLOWS'; flows: CrmCallFlow[] }
  | { type: 'ADD_FLOW'; flow: CrmCallFlow }
  | { type: 'UPDATE_FLOW'; id: string; updates: Partial<CrmCallFlow> }
  | { type: 'REMOVE_FLOW'; id: string }
  | { type: 'SET_LOADING'; loading: boolean }

const callFlowSetup = setup({
  types: {
    context: {} as { callFlows: CrmCallFlow[]; isLoading: boolean },
    events: {} as CrmSyncCallFlowEvent,
  },
})

export const crmSyncCallFlowMachine = callFlowSetup.createMachine({
  id: 'crmSyncCallFlow',
  context: { callFlows: [], isLoading: false },
  on: {
    SET_FLOWS: {
      actions: assign({
        callFlows: ({ event }) => (event.type === 'SET_FLOWS' ? event.flows : []),
      }),
    },
    ADD_FLOW: {
      actions: assign({
        callFlows: ({ context, event }) =>
          event.type === 'ADD_FLOW'
            ? [...context.callFlows, event.flow]
            : context.callFlows,
      }),
    },
    UPDATE_FLOW: {
      actions: assign({
        callFlows: ({ context, event }) =>
          event.type === 'UPDATE_FLOW'
            ? context.callFlows.map((f) => (f.id === event.id ? { ...f, ...event.updates } : f))
            : context.callFlows,
      }),
    },
    REMOVE_FLOW: {
      actions: assign({
        callFlows: ({ context, event }) =>
          event.type === 'REMOVE_FLOW'
            ? context.callFlows.filter((f) => f.id !== event.id)
            : context.callFlows,
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
  },
})
